import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query"; // 1. Import useQuery
import { baseAxios } from "../../apis";
import Pagination from "../commons/Pagination";
import moment from "moment";
import { formatCrypto } from "../../utils";
import SkeletonCard from "../skeletons/SkeletonCard";
import { useBot } from "../../context/botContext";

// 2. Create a dedicated hook for fetching the paginated trade history.
const useBotHistoryQuery = (botId, page, limit) => {
  return useQuery({
    // The query key is an array that includes all dependencies.
    // React Query will automatically refetch when any of these change.
    queryKey: ["botHistory", botId, page, limit],
    queryFn: async () => {
      const { data } = await baseAxios.get(`/api/bot_history`, {
        params: {
          bot_id: botId,
          page: page,
          limit: limit,
        },
      });
      return data; // The query returns the entire data object
    },
    // Only run the query if a bot is actually selected.
    enabled: !!botId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes,
    // This is a great UX feature for pagination. It keeps displaying the
    // last successful fetch's data while the next page is loading.
    placeholderData: (previousData) => previousData,
  });
};

export const TradeHistoryBot = () => {
  const { selectedBot } = useBot();

  // State for pagination and limit remains, as it's controlled by the user.
  const [limit, setLimit] = useState(5);
  const [page, setPage] = useState(1);

  // 3. REMOVE the old manual state management.
  // const [totalPages, setTotalPages] = useState(1);
  // const [tradeHistories, setTradeHistories] = useState([]);
  // const [loading, setLoading] = useState(false);

  // 4. REMOVE the old useEffect for data fetching.
  // useEffect(() => { ... }, [page, limit, selectedBot]);

  // 5. Call our new hook to get data and state.
  const { data, isLoading: loading } = useBotHistoryQuery(
    selectedBot?.id,
    page,
    limit
  );

  // 6. Derive state from the query's return value.
  const tradeHistories = data?.trade_history || [];
  const totalPages = data?.total_pages || 1;

  const offset = -new Date().getTimezoneOffset() / 60
  const utcLabel = `UTC${offset >= 0 ? "+" : ""}${offset}`

  // The rest of your component's JSX remains exactly the same.
  return (
    <div className="rounded-xl">
      <div className="flex justify-between items-center mb-3 w-full">
        <div className="relative z-[11] hidden md:flex items-center bg-white text-[#697077] text-sm px-5 pr-3 rounded-lg ml-auto ">
          <span className="mr-2">View</span>
          <select
            name="view-table"
            id="view-table"
            className="border-none cursor-pointer outline-none m-0 text-sm py-1.5"
            onChange={(e) => {
              setLimit(e.target.value);
              setPage(1); // Reset to first page when limit changes
            }}
            value={limit}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
          </select>
        </div>
      </div>
      <div className="rounded-xl border w-full overflow-x-auto">
        <table className="w-full text-sm text-left text-[#344054] ">
          <thead className="text-xs bg-[#F2F4F8] font-medium text-[#667085] border-b border-[#eaecf0]">
            <tr>
              <th className="p-4 rounded-tl-xl">No.</th>
              <th className="p-4">Pairing</th>
              <th className="p-4">Amount</th>
              <th className="p-4">Profit/Loss</th>
              <th className="p-4">Entry Price </th>
              <th className="p-4">Exit Price </th>
              <th className="p-4 rounded-tr-xl">Buy/Sell Time ({utcLabel})</th>
            </tr>
          </thead>
          <tbody className="text-sm text-[#101828]">
            {loading && !data ? ( // Show skeleton only on initial load
              [...Array(limit)].map((_, idx) => (
                <tr key={idx} className="border-b border-[#f2f4f7] bg-white">
                  {/* Skeleton Cells */}
                  <td
                    className={`p-4 text-sm ${
                      limit - 1 === idx && "rounded-bl-xl"
                    }`}
                  >
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td className="p-4 text-sm">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td className="p-4 text-sm">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["70px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td className="p-4 text-sm font-medium">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td className="p-4 text-sm">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td className="p-4 text-sm">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td
                    className={`p-4 text-sm ${
                      limit - 1 === idx && "rounded-br-xl"
                    }`}
                  >
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["100px"]}
                      heights={["10px"]}
                    />
                  </td>
                </tr>
              ))
            ) : tradeHistories?.length ? (
              tradeHistories.map((item, idx) => (
                <tr
                  key={item.id}
                  className="border-b border-[#f2f4f7] bg-white"
                >
                  <td
                    className={`p-4 text-sm ${
                      tradeHistories?.length - 1 === idx && "rounded-bl-xl"
                    }`}
                  >
                    {item.id}
                  </td>
                  <td className="p-4 text-sm">
                    {item.coin_name}/{item?.base_currency_name}
                  </td>
                  <td className="p-4 text-sm">
                    {item.amount} {item?.coin_name}
                  </td>
                  <td
                    className={`p-4 text-sm font-medium ${
                      item.profit > 0
                        ? "text-green-600"
                        : item.profit < 0
                        ? "text-red-500"
                        : "text-gray-700"
                    }`}
                  >
                    {formatCrypto(Number(item.pnl))} {item?.base_currency_name}
                  </td>
                  <td className="p-4 text-sm">
                    {formatCrypto(Number(item.entry_price))}{" "}
                    {item?.base_currency_name}
                  </td>
                  <td className="p-4 text-sm">
                    {formatCrypto(Number(item.close_price))}{" "}
                    {item?.base_currency_name}
                  </td>
                  <td
                    className={`p-4 text-sm ${
                      tradeHistories?.length - 1 === idx && "rounded-br-xl"
                    }`}
                  >
                    {moment(item?.entry_time || item?.created_at).format("DD/MM/YYYY HH:mm")}
                    &nbsp;-&nbsp;
                    {moment(item?.exit_time || item?.updated_at).format("DD/MM/YYYY HH:mm")}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  className="h-[10rem] p-3 text-sm text-center text-gray-400"
                  colSpan={7}
                >
                  No histories found for this bot.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="items-center justify-center mt-4 md:mt-6 flex">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};
